import Default from '@/components/layouts/Default.vue'
import Error5000 from '@/views/errors/Error500.vue'
import Login from '@/views/session/Login.vue'
import { RouteRecordRaw } from 'vue-router'

import { PERMISSIONS, whenProvider } from './models/enums/permissions'
import AssociateAccount from './views/session/AssociateAccount.vue'
import RequiredChangePassword from './views/session/RequiredChangePassword.vue'

const equatorialRoutes = whenProvider('EQUATORIAL', [
  {
    name: 'Parametrization',
    path: '/parametrizacao',
    component: () => import('@/views/parametrization/Parametrization.vue'),
    meta: { requiredPermission: PERMISSIONS.PAGES.PARAMETRIZATION_VIEW },
  },
])

const celescRoutes = whenProvider('CELESC', [
  {
    name: 'Fotocélulas',
    path: '/fotocelulas',
    component: () => import('@/views/tg/Photocell.vue'),
    meta: { requiredPermission: PERMISSIONS.PAGES.PHOTOC_VIEW },
  },
  {
    name: 'MDX Água',
    path: '/mdx-agua',
    component: () => import('@/views/tg/MdxWater.vue'),
    meta: { requiredPermission: PERMISSIONS.PAGES.MDX_WATER_VIEW },
  },
  {
    name: 'IagdOverview',
    path: '/iagd/visao-geral',
    component: () => import('@/views/iagd/Overview.vue'),
    meta: { requiredPermission: PERMISSIONS.PAGES.IAGD_VIEW },
  },
  {
    name: 'IagdSettings',
    path: '/iagd/configuracoes',
    component: () => import('@/views/iagd/Settings.vue'),
    meta: { requiredPermission: PERMISSIONS.PAGES.IAGD_VIEW },
  },
  {
    name: 'IagdCompleteness',
    path: '/iagd/completude',
    component: () => import('@/views/iagd/Completeness.vue'),
    meta: { requiredPermission: PERMISSIONS.PAGES.IAGD_VIEW },
  },
  {
    name: 'IagdRanking',
    path: '/iagd/ranking',
    component: () => import('@/views/iagd/Ranking.vue'),
    meta: { requiredPermission: PERMISSIONS.PAGES.IAGD_VIEW },
  },
  {
    name: 'IagdUcDetail',
    path: '/iagd/uc/:installation/:serial',
    component: () => import('@/views/iagd/UcDetail.vue'),
    meta: { requiredPermission: PERMISSIONS.PAGES.IAGD_VIEW },
  },
])

const equatorialIagdRedirect = whenProvider('EQUATORIAL', [
  {
    path: '/iagd/:pathMatch(.*)*',
    redirect: '/dashboard',
  },
])

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: Default,
    name: 'Default',
    redirect: '/dashboard',
    children: [
      {
        name: 'Dashboard',
        path: '/dashboard',
        component: () => import('@/views/dashboard/Overview.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.DASHBOARD_VIEW },
      },
      {
        name: 'Meters',
        path: 'medidores',
        component: () => import('@/views/Meters/Meters.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'CustomReport',
        path: '/relatorio-customizado',
        component: () => import('@/views/reports/CustomReport.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'InstallationInfo',
        path: 'instalacao/:installation/:serial',
        component: () => import('@/views/Meters/InstallationInfo.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'MeterInfo',
        path: 'medidores/:serial',
        component: () => import('@/views/Meters/MeterInfo.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'MeterFileUpload',
        path: 'medidores/:serial/file-upload',
        component: () => import('@/views/Meters/MeterFileUpload.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'ManualInsertReadings',
        path: 'medidores/:serial/manual-insert',
        component: () => import('@/views/Meters/ManualInsertReadings.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'MassCommands',
        path: '/comandos-em-massa',
        redirect: '/comandos-em-massa/corte-religamento',
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'MassCommandsReadings',
        path: '/comandos-em-massa/leituras',
        component: () => import('@/views/massCommands/Readings.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'MassCommandsCutReconnect',
        path: '/comandos-em-massa/corte-religamento',
        component: () => import('@/views/massCommands/CutReconnect.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'ScheduledCommands',
        path: '/comandos-agendados',
        redirect: '/comandos-agendados/comandos-do-sistema',
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'ScheduledCommandsSystemCommands',
        path: '/comandos-agendados/comandos-do-sistema',
        component: () => import('@/views/scheduledCommands/SystemCommands.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'ScheduledCommandsRoutineListing',
        path: '/comandos-agendados/listagem-de-rotinas',
        component: () => import('@/views/scheduledCommands/RoutineListing.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'ScheduledCommandsRoutineDetails',
        path: '/comandos-agendados/rotinas/:id',
        component: () => import('@/views/scheduledCommands/RoutineDetails.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'Smc',
        path: 'smcs',
        component: () => import('@/views/Smcs/Smc.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.SMC_VIEW },
      },
      {
        name: 'Notas de Serviço',
        path: 'notas-de-servico',
        component: loadServiceNotesComponent,
        meta: { requiredPermission: PERMISSIONS.PAGES.SERVICE_NOTES_VIEW },
      },
      {
        name: 'SmcInfo',
        path: 'smcs/:serial',
        component: () => import('@/views/Smcs/SmcInfo.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.SMC_VIEW },
      },
      {
        name: 'UserControl',
        path: '/gerenciamento/usuarios',
        component: () => import('@/views/management/UserControl.vue'),
        meta: { requiredPermission: PERMISSIONS.MANAGE.USER_MANAGE },
      },
      {
        name: 'Building',
        path: 'em-construcao',
        component: () => import('@/views/errors/Buinding.vue'),
      },
      {
        name: 'NotPermission',
        path: 'sem-permissao',
        component: () => import('@/views/errors/NotPermission.vue'),
      },
      {
        name: 'Billing',
        path: '/faturamento',
        component: loadBillingComponent,
        meta: { requiredPermission: PERMISSIONS.PAGES.BILLING_VIEW },
      },
      {
        name: 'Balance',
        path: '/balance',
        component: () => import('@/views/balance/Balance.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.BALANCE_VIEW },
      },
      {
        name: 'BalanceTransformers',
        path: '/balance/transformadores',
        component: () => import('@/views/balance/Transformers.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.BALANCE_VIEW },
      },
      {
        name: 'TransformerDetails',
        path: '/balance/transformadores/:transformerId',
        component: () => import('@/components/transformers/TransformerDetails.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.BALANCE_VIEW },
      },
      {
        name: 'BalanceAreas',
        path: '/balance/areas',
        component: () => import('@/views/balance/Areas.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.BALANCE_VIEW },
      },
      {
        name: 'AreaDetails',
        path: '/balance/areas/:areaId',
        component: () => import('@/components/areas/AreaDetails.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.BALANCE_VIEW },
      },
      {
        name: 'BalanceRegions',
        path: '/balance/regioes',
        component: () => import('@/views/balance/Regions.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.BALANCE_VIEW },
      },
      {
        name: 'RegionDetails',
        path: '/balance/regioes/:regionId',
        component: () => import('@/components/regions/RegionDetails.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.BALANCE_VIEW },
      },
      {
        name: 'ManagementTags',
        path: '/gerenciamento/tags',
        component: () => import('@/views/management/Tags.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_TAGS_VIEW },
      },
      {
        name: 'ManagementFirmware',
        path: '/gerenciamento/firmware',
        component: () => import('@/views/management/Firmware.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_FIRMWARE_VIEW },
      },
      {
        name: 'NetworkManagement',
        path: '/gerenciamento/rede',
        component: () => import('@/views/management/NetworkManagement.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_NETWORK_VIEW },
      },
      {
        name: 'NetworkDetails',
        path: '/gerenciamento/rede/detalhes/:serial',
        component: () => import('@/components/network/NetworkDetails.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_NETWORK_VIEW },
      },
      ...equatorialRoutes,
      {
        name: 'GroupsList',
        path: '/gerenciamento/grupos',
        component: () => import('@/views/management/Groups.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_GROUPS_VIEW },
      },
      {
        name: 'GroupDetails',
        path: '/gerenciamento/grupos/:id',
        component: () => import('@/views/management/GroupDetails.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_GROUPS_VIEW },
      },
      {
        name: 'ManagementGateways',
        path: '/gerenciamento/gateways',
        component: () => import('@/views/management/Gateways.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_GATEWAYS_VIEW },
      },
      {
        name: 'GatewaysInfo',
        path: '/gerenciamento/gateways/:serial',
        component: () => import('@/views/management/GatewaysInfo.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_GATEWAYS_VIEW },
      },
      {
        name: 'DataExtraction',
        path: '/exportacao-de-dados',
        component: () => import('@/views/dataExtraction/DataExtraction.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.METER_VIEW },
      },
      {
        name: 'DownloadCenter',
        path: '/download-center',
        component: () => import('@/views/downloadCenter/DownloadCenter.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.DOWNLOAD_VIEW },
      },
      {
        name: 'ManagementKeys',
        path: '/gerenciamento/chaves',
        component: () => import('@/views/management/Keys.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_KEYS_VIEW },
      },
      {
        name: 'Clients',
        path: '/gerenciamento/clientes',
        component: () => import('@/views/management/Clients.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_CLIENTS_VIEW },
      },
      {
        name: 'ClientsDetails',
        path: '/gerenciamento/clientes/:id',
        component: () => import('@/views/management/ClientsDetails.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_CLIENTS_VIEW },
      },
      {
        name: 'ProcessInfo',
        path: '/processo/:id',
        component: () => import('@/views/process/ProcessInfo.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.PROCESS_VIEW },
      },
      {
        name: 'Models',
        path: '/gerenciamento/modelos',
        component: () => import('@/views/management/Models.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_MODELS_VIEW },
      },
      {
        name: 'Configuration',
        path: '/gerenciamento/configuracoes',
        component: () => import('@/views/management/Configuration.vue'),
        meta: { requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_CONFIGURATION_VIEW },
      },
      ...celescRoutes,
      ...equatorialIagdRedirect,
      {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        component: () => import('@/views/errors/NotFound.vue'),
      },
    ],
  },
  {
    path: '/500',
    name: 'Error500',
    component: Error5000,
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
  },
  {
    path: '/associate',
    name: 'Associate',
    component: AssociateAccount,
  },
  {
    path: '/change-password',
    name: 'RequiredChangePassword',
    component: RequiredChangePassword,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFoundPublic',
    component: () => import('@/views/errors/NotFound.vue'),
  },
]

function loadServiceNotesComponent() {
  return process.env.VUE_APP_SERVICEMODULE == 2
    ? import('@/views/service-notes/ServiceNotes2ndModule.vue')
    : import('@/views/service-notes/ServiceNotes.vue')
}

function loadBillingComponent() {
  return process.env.VUE_APP_BILLINGMODULE == 2
    ? import('@/views/errors/Buinding.vue')
    : import('@/views/billing/BillingDash.vue')
}

export default routes
